/**
 * DhanHealthBanner — App-wide Dhan broker health monitor
 * =========================================================
 * Apr-2026 B-UX-2: Persistent banner visible from any admin screen
 * when Dhan IP whitelisting falls out of sync, token expires, or
 * API calls fail. Clicking takes user directly to Broker Integration tab.
 *
 * Polls /api/broker/dhan/health every 60s. Silent when healthy.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, ChevronRight, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  healthy: { show: false },
  no_credentials: { show: false },  // user hasn't set up Dhan at all — no alert needed
  ip_mismatch: {
    show: true,
    severity: 'high',
    title: 'Dhan IP out of sync — orders will be rejected',
  },
  ip_missing: {
    show: true,
    severity: 'medium',
    title: 'Dhan server IP not registered',
  },
  token_expired: {
    show: true,
    severity: 'high',
    title: 'Dhan access token expired',
  },
  unknown: {
    show: true,
    severity: 'low',
    title: 'Dhan broker health check failed',
  },
};

export const DhanHealthBanner = ({ onNavigateToBroker }) => {
  const { authFetch, user } = useAuth();
  const [health, setHealth] = useState(null);
  const [dismissedAt, setDismissedAt] = useState(null);
  const isAdmin = user?.role === 'admin';

  const fetchHealth = useCallback(async () => {
    if (!user) return;
    try {
      const res = await authFetch(`${API}/api/broker/dhan/health`);
      if (res.ok) {
        const data = await res.json();
        setHealth(data);
      }
    } catch (_e) { /* network hiccup — keep previous state */ }
  }, [authFetch, user]);

  useEffect(() => {
    fetchHealth();
    const id = setInterval(fetchHealth, 60000);
    // Session 145: listen for in-app "token was just updated" pulse so the
    // banner clears immediately instead of waiting up to 60s for the next
    // scheduled poll. Emitted by BrokerIntegrationTab.handleSaveAndValidate
    // after a successful reconnect.
    const onRefresh = () => fetchHealth();
    window.addEventListener('dhan-health-refresh', onRefresh);
    return () => {
      clearInterval(id);
      window.removeEventListener('dhan-health-refresh', onRefresh);
    };
  }, [fetchHealth]);

  // Reset dismissal if the underlying health status changes (don't silence forever)
  useEffect(() => {
    if (health?.status && dismissedAt && health.status !== dismissedAt.status) {
      setDismissedAt(null);
    }
  }, [health?.status, dismissedAt]);

  if (!health) return null;
  const cfg = STATUS_CONFIG[health.status] || STATUS_CONFIG.unknown;
  if (!cfg.show) return null;
  // Dismissal respects recent dismissals only for the same status (resurfaces if status changes)
  if (dismissedAt && dismissedAt.status === health.status &&
      Date.now() - dismissedAt.at < 30 * 60 * 1000) {
    return null;
  }

  const colorClasses = cfg.severity === 'high'
    ? 'bg-red-950/40 border-red-500/50 text-red-100'
    : cfg.severity === 'medium'
    ? 'bg-amber-950/40 border-amber-500/50 text-amber-100'
    : 'bg-gray-800/60 border-gray-500/30 text-gray-200';

  const iconColor = cfg.severity === 'high'
    ? 'text-red-400'
    : cfg.severity === 'medium'
    ? 'text-amber-400'
    : 'text-gray-400';

  return (
    <div
      className={`mb-4 rounded-lg border px-4 py-3 flex items-start gap-3 ${colorClasses}`}
      data-testid="dhan-health-banner"
    >
      <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${iconColor}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold">{cfg.title}</p>
          {health.current_server_ip && (
            <span className="text-[10px] font-mono opacity-70">
              current: {health.current_server_ip}
              {health.registered_primary_ip
                ? ` · registered: ${health.registered_primary_ip}`
                : ''}
            </span>
          )}
        </div>
        <p className="text-xs opacity-80 mt-0.5">{health.message}</p>
        {isAdmin ? (
          <button
            onClick={onNavigateToBroker}
            className="inline-flex items-center gap-1 mt-2 text-xs font-medium underline underline-offset-2 hover:no-underline"
            data-testid="dhan-health-nav-btn"
          >
            Go to Broker Integration
            <ChevronRight className="w-3 h-3" />
          </button>
        ) : (
          <p className="text-[11px] opacity-70 mt-2 italic" data-testid="dhan-health-user-note">
            Auto-execution paused until the admin refreshes the broker token.
          </p>
        )}
      </div>
      <button
        onClick={() => setDismissedAt({ status: health.status, at: Date.now() })}
        className="flex-shrink-0 opacity-60 hover:opacity-100"
        title="Hide for 30 min"
        data-testid="dhan-health-dismiss-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default DhanHealthBanner;
